import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthApi } from '@/features/auth/services/auth.api';

interface TokenResponse {
    access_token: string;
    refresh_token: string;
    expires_in: number; // วินาที
}

@Injectable({
    providedIn: 'root'
})
export class AuthTokenService {
    authApi = inject(AuthApi);
    router = inject(Router);
    private readonly ACCESS_KEY = 'access_token';
    private readonly REFRESH_KEY = 'refresh_token';
    private readonly EXPIRE_KEY = 'token_expire';

    private _token = signal<string | null>(this.getStorage().getItem(this.ACCESS_KEY));

    get token(): string | null {
        return this._token();
    }

    isAuthenticated = computed(() => !!this._token());

    setTokens(response: TokenResponse, rememberMe: boolean = false) {
        const now = Date.now();

        // ตั้ง expire ปกติ จาก expires_in ที่ได้จาก server (เป็นวินาที → แปลง ms)
        let expiresAt = now + response.expires_in * 1000;

        // ถ้า rememberMe = true → ยืดเวลา (เช่น 30 เท่า)
        if (rememberMe) {
            expiresAt = now + response.expires_in * 1000 * 30;
        }

        const storage = rememberMe ? localStorage : sessionStorage;

        storage.setItem(this.ACCESS_KEY, response.access_token);
        storage.setItem(this.REFRESH_KEY, response.refresh_token);
        storage.setItem(this.EXPIRE_KEY, expiresAt.toString());

        this._token.set(response.access_token);
    }

    private getStorage(): Storage {
        // ตรวจสอบจากว่า token อยู่ในไหน
        return localStorage.getItem(this.ACCESS_KEY) ? localStorage : sessionStorage;
    }

    clearTokens() {
        const storage = this.getStorage();
        storage.removeItem(this.ACCESS_KEY);
        storage.removeItem(this.REFRESH_KEY);
        storage.removeItem(this.EXPIRE_KEY);
        this._token.set(null);
    }

    isTokenExpired(): boolean {
        const storage = this.getStorage();
        const expire = Number(storage.getItem(this.EXPIRE_KEY));

        const isExpired = isNaN(expire) || Date.now() > expire;

        return isExpired;
    }

    async refresh(): Promise<boolean> {
        const storage = this.getStorage();
        const refresh_token = storage.getItem(this.REFRESH_KEY);
        if (!refresh_token) return Promise.resolve(false);

        var response = await firstValueFrom(this.authApi.getRefreshToken(refresh_token));

        if (!response) {
            this.clearTokens();
            this.router.navigate(['/login']);
            return Promise.resolve(false);
        }

        console.log('refresh token', response);
        this.setTokens({
            access_token: response.token,
            refresh_token: response.refreshToken,
            expires_in: response.expiresIn
        });

        return Promise.resolve(true);
    }
}
